import { useEffect, useMemo, useState } from 'react';
import {
  Menu,
  Bell,
  ChevronDown,
  LogOut,
  ChevronRight,
  Plug,
  User,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/ui/dropdown-menu';
import { useAuth } from '@/hooks/useAuth';
import { menuGroups } from './menu-config';
import {
  checkBossExtensionAvailable,
  openBossExtensionInstallGuide,
} from '@/lib/boss-extension';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/ui/tooltip';
import { ProfileCenterDialog } from '@/features/profile-center/components/profile-center-dialog';

interface HeaderProps {
  onToggleSidebar?: () => void;
}

export function Header({ onToggleSidebar }: HeaderProps) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [profileCenterOpen, setProfileCenterOpen] = useState(false);

  // 根据当前路径获取菜单名称
  const getCurrentMenuName = () => {
    const currentPath = location.pathname;
    for (const group of menuGroups) {
      for (const item of group.items) {
        if (item.path === currentPath) {
          return item.label;
        }
      }
    }
    return '';
  };

  const currentMenuName = getCurrentMenuName();

  const breadcrumbs = useMemo(() => {
    const path = location.pathname.replace(/\/$/, '');
    const offerBase = '/offer-management';
    const interviewBase = '/interview-management';
    const jobProfileBase = '/job-profile';
    const resumeBase = '/resume-management';
    const matchingBase = '/intelligent-matching';
    const recruitmentBase = '/recruitment-pipeline';
    const internalReferralBase = '/internal-referral';
    const locationState = location.state as { from?: string } | null;
    const from =
      locationState?.from || new URLSearchParams(location.search).get('from');
    const fromRecruitment =
      from === recruitmentBase || from === 'recruitment-pipeline';
    if (path === recruitmentBase) {
      return [{ label: '招聘流程', path: recruitmentBase }];
    }
    if (path === internalReferralBase) {
      return [{ label: '内推渠道', path: internalReferralBase }];
    }
    if (path.startsWith(`${internalReferralBase}/`)) {
      return [
        { label: '内推渠道', path: internalReferralBase },
        { label: '岗位详情' },
      ];
    }
    if (path === offerBase) {
      return [{ label: 'Offer管理', path: offerBase }];
    }
    if (path.startsWith(`${offerBase}/`) && path.endsWith('/edit')) {
      return [{ label: 'Offer管理', path: offerBase }, { label: '编辑Offer' }];
    }
    if (path.startsWith(`${offerBase}/`)) {
      return [{ label: 'Offer管理', path: offerBase }, { label: 'Offer详情' }];
    }
    if (path === interviewBase) {
      return [{ label: '面试管理', path: interviewBase }];
    }
    if (path.startsWith(`${interviewBase}/`)) {
      return [
        { label: '面试管理', path: interviewBase },
        { label: '面试详情' },
      ];
    }
    if (path === jobProfileBase) {
      return [{ label: '职位管理', path: jobProfileBase }];
    }
    if (path.startsWith(`${jobProfileBase}/`) && path.endsWith('/edit')) {
      if (fromRecruitment) {
        return [
          { label: '招聘流程', path: recruitmentBase },
          { label: '编辑岗位详情' },
        ];
      }
      return [
        { label: '职位管理', path: jobProfileBase },
        { label: '编辑岗位' },
      ];
    }
    if (path.startsWith(`${jobProfileBase}/`)) {
      if (fromRecruitment) {
        return [
          { label: '招聘流程', path: recruitmentBase },
          { label: '职位详情' },
        ];
      }
      return [
        { label: '职位管理', path: jobProfileBase },
        { label: '职位详情' },
      ];
    }
    if (path === resumeBase) {
      return [{ label: '简历管理', path: resumeBase }];
    }
    if (path.startsWith(`${resumeBase}/`)) {
      if (fromRecruitment) {
        return [
          { label: '招聘流程', path: recruitmentBase },
          { label: '简历详情' },
        ];
      }
      return [{ label: '简历管理', path: resumeBase }, { label: '简历详情' }];
    }
    if (path === matchingBase) {
      return [{ label: '智能匹配', path: matchingBase }];
    }
    if (path.startsWith(`${matchingBase}/`)) {
      return [{ label: '智能匹配', path: matchingBase }, { label: '匹配详情' }];
    }
    return [];
  }, [location.pathname, location.search, location.state]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('退出登录失败:', error);
    }
  };

  const [extensionStatus, setExtensionStatus] = useState<
    'idle' | 'loading' | 'ok' | 'missing' | 'timeout' | 'error'
  >('idle');

  useEffect(() => {
    let cancelled = false;

    const detect = async () => {
      setExtensionStatus('loading');
      try {
        const resp = await checkBossExtensionAvailable();
        if (cancelled) return;
        if (resp.ok) {
          setExtensionStatus('ok');
        } else {
          if (resp.reason === 'NOT_INSTALLED') setExtensionStatus('missing');
          else if (resp.reason === 'TIMEOUT') setExtensionStatus('timeout');
          else setExtensionStatus('error');
        }
      } catch {
        if (cancelled) return;
        setExtensionStatus('error');
      }
    };

    detect();
    return () => {
      cancelled = true;
    };
  }, []);

  const tooltipText = () => {
    if (extensionStatus === 'loading' || extensionStatus === 'idle') {
      return '正在检测插件…';
    }
    if (extensionStatus === 'ok') {
      return '已检测到插件';
    }
    if (extensionStatus === 'missing') {
      return '未检测到插件，请安装并启用';
    }
    if (extensionStatus === 'timeout') {
      return '插件检测超时，点击重试';
    }
    return '插件不可用，检查浏览器或扩展状态';
  };

  const handleExtensionClick = async () => {
    if (extensionStatus === 'loading') return;
    setExtensionStatus('loading');
    try {
      const resp = await checkBossExtensionAvailable();
      if (resp.ok) {
        setExtensionStatus('ok');
      } else {
        if (resp.reason === 'NOT_INSTALLED') setExtensionStatus('missing');
        else if (resp.reason === 'TIMEOUT') setExtensionStatus('timeout');
        else setExtensionStatus('error');
      }
    } catch {
      setExtensionStatus('error');
    }
  };

  const handleInstallClick = () => {
    openBossExtensionInstallGuide();
  };

  return (
    <header className="flex h-16 items-center justify-between bg-white px-4 rounded-xl border border-[#E5E7EB] shadow-sm">
      <div className="flex flex-1 items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          onClick={onToggleSidebar}
          aria-label="展开侧边栏"
        >
          <Menu className="h-5 w-5" />
        </Button>
        {breadcrumbs.length > 0 ? (
          <div className="flex items-center gap-3">
            <div className="h-4 w-0.5 bg-gray-500" />
            <div className="flex items-center gap-2 text-base font-medium text-gray-400">
              {breadcrumbs.map((item, idx) => (
                <div key={item.label} className="flex items-center gap-2">
                  {idx > 0 && (
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  )}
                  <button
                    className="text-gray-400 hover:text-gray-500 disabled:cursor-default disabled:text-gray-400"
                    onClick={
                      item.path
                        ? () => navigate(item.path as string)
                        : undefined
                    }
                    disabled={!item.path}
                  >
                    {item.label}
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : currentMenuName ? (
          <div className="flex items-center gap-3">
            <div className="h-4 w-0.5 bg-gray-500" />
            <span className="text-base font-medium text-gray-400">
              {currentMenuName}
            </span>
          </div>
        ) : null}
      </div>

      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          className="header-notification-btn disabled"
          aria-label="查看通知"
          disabled
        >
          <Bell className="h-4 w-4" />
          <span className="header-notification-badge" />
        </Button>

        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="检测 Boss 插件"
                onClick={handleExtensionClick}
              >
                <Plug
                  className="h-5 w-5"
                  color={
                    extensionStatus === 'ok'
                      ? '#22c55e'
                      : extensionStatus === 'loading'
                        ? '#6b7280'
                        : '#9ca3af'
                  }
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <div className="flex flex-col gap-2 text-sm text-gray-800">
                <span>{tooltipText()}</span>
                {extensionStatus !== 'ok' && extensionStatus !== 'loading' && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 px-3"
                    onClick={handleInstallClick}
                  >
                    安装
                  </Button>
                )}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <div className="header-profile cursor-pointer hover:bg-accent/50 transition-colors">
              <Avatar
                className="h-8 w-8 border-2"
                style={{ borderColor: '#7bb8ff' }}
              >
                {user?.avatar_url && (
                  <AvatarImage src={user.avatar_url} alt="用户头像" />
                )}
                <AvatarFallback
                  className="text-primary-foreground"
                  style={{
                    background:
                      'linear-gradient(135deg, #7bb8ff 0%, #3F3663 100%)',
                  }}
                >
                  {(user?.account || user?.username)
                    ?.charAt(0)
                    ?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="header-profile-info">
                <p className="header-profile-name">
                  {user?.account || user?.username || '用户'}
                </p>
                <p className="header-profile-email">{user?.email || ''}</p>
              </div>
              <ChevronDown className="header-profile-chevron" />
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={() => setProfileCenterOpen(true)}>
              <User className="mr-2 h-4 w-4" />
              个人中心
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={handleLogout}
              className="text-destructive focus:text-destructive"
            >
              <LogOut className="mr-2 h-4 w-4" />
              退出登录
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <ProfileCenterDialog
          open={profileCenterOpen}
          onOpenChange={setProfileCenterOpen}
          username={user?.account || user?.username || '用户'}
          email={user?.email || ''}
        />
      </div>
    </header>
  );
}
